import { getBrowserCookie } from 'Utils/cookieUtils';

const isDev =
  typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

/**
 * Default Leo SDK URL — sürüm analitik ekibiyle hizalanmalı; host
 * `window.__CAC_LEO_SCRIPT_SRC__` ile override edebilir.
 * @see leo_implementation.md
 */
const DEFAULT_LEO_SCRIPT_SRC =
  'https://images.hepsiburada.net/storefront/storefront/www/leo/0.0.187/leo.js';

let loadPromise = null;
let didInitAfterOurInject = false;

function getLeoScriptSrc() {
  if (typeof window === 'undefined') {
    return DEFAULT_LEO_SCRIPT_SRC;
  }
  return window.__CAC_LEO_SCRIPT_SRC__ || DEFAULT_LEO_SCRIPT_SRC;
}

function isLeoScriptInDom() {
  if (typeof document === 'undefined') {
    return false;
  }
  return !!document.querySelector('script[src*="leo.js"]');
}

function waitForLeoCapture(maxWaitMs = 8000, intervalMs = 50) {
  return new Promise(resolve => {
    const deadline = Date.now() + maxWaitMs;
    const tick = () => {
      const leo = getLeo();
      if (leo && typeof leo.capture === 'function') {
        resolve(leo);
        return;
      }
      if (Date.now() >= deadline) {
        resolve(null);
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

function tryInitLeoAfterInject(leo) {
  if (didInitAfterOurInject || !leo || typeof leo.init !== 'function') {
    return;
  }
  try {
    leo.init({
      api_transport: 'sendBeacon',
      batching: false
    });
    didInitAfterOurInject = true;
  } catch (err) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('[LeoService] leo.init failed', err);
    }
  }
}

function injectLeoScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Leo script load error'));
    document.head.appendChild(script);
  });
}

/**
 * `window.leo` hazır olana kadar bekler; gerekirse Leo script’ini bir kez yükler.
 * Sayfada zaten `leo.js` varsa tekrar eklemez (leo_implementation.md).
 *
 * @returns {Promise<object | null>}
 */
export function ensureLeoScript() {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  const existing = getLeo();
  if (existing && typeof existing.capture === 'function') {
    return Promise.resolve(existing);
  }

  if (loadPromise) {
    return loadPromise;
  }

  if (isLeoScriptInDom()) {
    loadPromise = waitForLeoCapture().then(leo => leo);
    return loadPromise;
  }

  const src = getLeoScriptSrc();
  loadPromise = injectLeoScript(src)
    .then(() => waitForLeoCapture(10000))
    .then(leo => {
      if (leo) {
        tryInitLeoAfterInject(leo);
      }
      return leo;
    })
    .catch(() => {
      loadPromise = null;
      return null;
    });

  return loadPromise;
}

/**
 * Lazy access to `window.leo` (senkron). Script henüz yüklenmediyse `null` olabilir.
 * @returns {object | null}
 */
export function getLeo() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.leo ?? null;
}

export function getLeoCookie() {
  const raw = getBrowserCookie('leo');
  if (!raw) {
    return null;
  }

  try {
    return typeof raw === 'string' ? JSON.parse(decodeURIComponent(raw)) : raw;
  } catch (err) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('[LeoService] failed to parse leo cookie', err);
    }
    return null;
  }
}

/**
 * Leo anonymous id — `leo` cookie içindeki `anon_id`.
 * @returns {string | null}
 */
export function getLeoAnonId() {
  return getLeoCookie()?.anon_id ?? null;
}

/**
 * Query'deki anonymousId ile mevcut Leo anon id aynı mı?
 * URL copy-paste attribution koruması için.
 * @param {string | null | undefined} anonymousId
 * @returns {boolean}
 */
export function isSameLeoAnonymousUser(anonymousId) {
  return Boolean(anonymousId && anonymousId === getLeoAnonId());
}

/**
 * Host page URL'deki ads attribution alanları.
 * Voltran fragment query'sinde olmayabilir; PDP page search'ten okunur.
 * @returns {{ anonymousId?: string, adsUniqueId?: string, isPromoted?: string }}
 */
export function getAdsAttributionFromPageUrl() {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);

  return {
    anonymousId: params.get('anonymousId') || undefined,
    adsUniqueId: params.get('adsUniqueId') || undefined,
    isPromoted: params.get('isPromoted') || undefined
  };
}

function runCapture(leo, category, type, payload) {
  try {
    leo.capture(category, type, payload);
  } catch (err) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('[LeoService] capture failed', err);
    }
  }
}

/**
 * Safe Leo SDK capture: SSR-safe, no throw. `window.leo` yoksa script yüklemeyi dener,
 * sonra olayı gönderir (async; çağıranı bloklamaz).
 *
 * @param {string} category - e.g. `'trade_in'`, `'component'`
 * @param {'view' | 'click'} type
 * @param {Record<string, unknown>} payload
 */
export function leoCapture(category, type, payload) {
  if (typeof window === 'undefined') {
    return;
  }

  const leo = getLeo();
  if (leo && typeof leo.capture === 'function') {
    runCapture(leo, category, type, payload);
    return;
  }

  ensureLeoScript().then(sdk => {
    if (sdk && typeof sdk.capture === 'function') {
      runCapture(sdk, category, type, payload);
    } else if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('[LeoService] Leo SDK not available after load attempt');
    }
  });
}
